import { createEffect, onCleanup, on, type Accessor } from 'solid-js'
import ApexCharts from 'apexcharts'
import type { ApexOptions } from 'apexcharts'

export interface UseChartOptions<T> {
  data: Accessor<T | undefined>
  chartRef: Accessor<HTMLDivElement | undefined>
  buildOptions: (data: T) => ApexOptions
  updateOnChange?: boolean
  deps?: Accessor<unknown>[]
}

export interface UseChartReturn {
  getInstance: () => ApexCharts | undefined
  refresh: () => void
  destroy: () => void
}

export const useChart = <T>(options: UseChartOptions<T>): UseChartReturn => {
  const { data, chartRef, buildOptions, updateOnChange = false, deps = [] } = options

  let chartInstance: ApexCharts | undefined

  const destroy = () => {
    if (chartInstance) {
      chartInstance.destroy()
      chartInstance = undefined
    }
  }

  const render = () => {
    const currentData = data()
    const ref = chartRef()

    if (!currentData || !ref) {
      destroy()
      return
    }

    const chartOptions = buildOptions(currentData)

    if (updateOnChange && chartInstance) {
      chartInstance.updateOptions(chartOptions, true, true)
    } else {
      destroy()
      chartInstance = new ApexCharts(ref, chartOptions)
      chartInstance.render()
    }
  }

  const refresh = () => {
    requestAnimationFrame(render)
  }

  createEffect(
    on(
      [data, ...deps],
      () => {
        const currentData = data()

        if (!currentData) {
          destroy()
          return
        }

        requestAnimationFrame(render)
      },
      { defer: false }
    )
  )

  onCleanup(destroy)

  return {
    getInstance: () => chartInstance,
    refresh,
    destroy
  }
}

export interface UseChartWithLegendOptions<T, L extends Record<string, boolean>> {
  data: Accessor<T | undefined>
  chartRef: Accessor<HTMLDivElement | undefined>
  legendState: Accessor<L>
  buildOptions: (data: T, legend: L) => ApexOptions
}

export const useChartWithLegend = <T, L extends Record<string, boolean>>(
  options: UseChartWithLegendOptions<T, L>
): UseChartReturn => {
  const { data, chartRef, legendState, buildOptions } = options

  let chartInstance: ApexCharts | undefined

  const destroy = () => {
    if (chartInstance) {
      chartInstance.destroy()
      chartInstance = undefined
    }
  }

  const render = () => {
    const currentData = data()
    const ref = chartRef()
    const legend = legendState()

    if (!currentData || !ref) {
      destroy()
      return
    }

    const chartOptions = buildOptions(currentData, legend)

    if (chartInstance) {
      chartInstance.updateOptions(chartOptions, true, true)
    } else {
      chartInstance = new ApexCharts(ref, chartOptions)
      chartInstance.render()
    }
  }

  const refresh = () => {
    requestAnimationFrame(render)
  }

  createEffect(
    on([data, legendState], ([currentData]) => {
      if (!currentData) {
        destroy()
        return
      }

      requestAnimationFrame(render)
    })
  )

  onCleanup(destroy)

  return {
    getInstance: () => chartInstance,
    refresh,
    destroy
  }
}
